/**
 * authHeader — Sprint 28.15b auth-401 fix.
 *
 * Tiny helper to attach the Supabase access token as `Authorization:
 * Bearer <jwt>` on outgoing fetch calls to same-origin `/api/*`
 * endpoints.
 *
 * Why explicit: the server reads the bearer token from the Authorization
 * header (NOT cookies) — see `apps/web/api/_lib/auth/session.ts:13`:
 *
 *   "Bearer-token only. The client attaches `Authorization: Bearer <jwt>`
 *    — we do NOT read cookies because Vercel's serverless env doesn't
 *    share a cookie domain with the static origin."
 *
 * If the user is anonymous (no session), this returns an empty headers
 * object — the server then 401s with `code: 'auth-required'` which the
 * client surfaces appropriately.
 *
 * Imports the supabase singleton statically — the auth-bridge client
 * (only one GoTrueClient on the page) is shared across all callsites.
 * (Was dynamic import; switched to static to silence Vite warning since
 * supabase is already statically imported by SignInButton + Curator +
 * userStore + others — dynamic import couldn't move it to a separate
 * chunk anyway.)
 */

import { isAuthConfigured, supabase } from './supabase';

/** One captured viewer/request context. The opaque scope contains no token. */
export interface AuthRequestContext {
  readonly viewerId: string | null;
  readonly scope: object;
  readonly headers: Record<string, string>;
  /** Synchronous identity-epoch guard; safe to call after an awaited request. */
  readonly isCurrent: () => boolean;
}

let observedViewer: string | null | undefined;
let authScope: object = {};
let observing = false;

function observeViewer(viewerId: string | null, revoke = false): void {
  if (revoke || observedViewer !== viewerId) authScope = {};
  observedViewer = viewerId;
}

function observeAuth(): boolean {
  if (observing) return true;
  let accepted = true;
  try {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!accepted) return;
      // Primitive invalidation only: Supabase holds its auth lock here.
      observeViewer(session?.user.id ?? null, event === 'SIGNED_OUT');
    });
    if (typeof data.subscription?.unsubscribe !== 'function') throw new Error('Auth observation is unavailable.');
    observing = true;
    return true;
  } catch {
    accepted = false;
    observedViewer = undefined; authScope = {};
    return false;
  }
}

/** Capture identity and bearer header from the same canonical session read.
 * Lazy observation invalidates old contexts on logout/account switches, even
 * when the same account later signs in again. Never uses a token as a cache key.
 * Unknown auth state fails closed instead of reusing another viewer's data. */
export async function captureAuthRequestContext(): Promise<AuthRequestContext | null> {
  if (!isAuthConfigured) {
    observeViewer(null);
    const scope = authScope;
    return { viewerId: null, scope, headers: {}, isCurrent: () => authScope === scope };
  }
  if (!observeAuth()) return null;
  // INITIAL_SESSION may arrive during the first read. One bounded retry also
  // handles a concurrent account switch without accepting the old read.
  for (let attempt = 0; attempt < 2; attempt++) {
    const before = authScope;
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) return null;
      if (before !== authScope) continue;
      const session = data.session;
      const viewerId = session?.user.id ?? null;
      const token = session?.access_token;
      if (session && (!viewerId || typeof token !== 'string' || token.length === 0)) return null;
      observeViewer(viewerId);
      const scope = authScope;
      return { viewerId, scope, headers: token ? { Authorization: `Bearer ${token}` } : {},
        isCurrent: () => authScope === scope && observedViewer === viewerId };
    } catch { return null; }
  }
  return null;
}

/**
 * Return `{ Authorization: 'Bearer <jwt>' }` when the user has a live
 * Supabase session, or `{}` when anonymous / unconfigured. Never throws.
 */
export async function withAuthHeader(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (typeof token === 'string' && token.length > 0) {
      return { Authorization: `Bearer ${token}` };
    }
  } catch {
    /* anonymous fallback */
  }
  return {};
}
